// src/components/Cadastro.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { auth, db } from '../firebaseConfig';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getApp } from 'firebase/app';
import { sendPasswordResetEmail, signOut } from 'firebase/auth';
import {
  collection,
  getDocs,
  doc,
  deleteDoc,
  addDoc,
  updateDoc,
  serverTimestamp,
  getDoc,
  writeBatch,
  query,
  where,
} from 'firebase/firestore';
import UserFormModal from './UserFormModal';
import { FaEdit, FaUserPlus, FaToggleOn, FaToggleOff, FaTrashAlt, FaEnvelope, FaDownload, FaUpload, FaCheck } from 'react-icons/fa';
import * as XLSX from 'xlsx';
import '../styles/Cadastro.css';

// --- COMPONENTE DE PAGINAÇÃO INTELIGENTE ---
const Pagination = ({ currentPage, totalPages, onPageChange, itemsPerPage, onItemsPerPageChange }) => {
  if (totalPages === 0) return null;

  const getPageNumbers = () => {
    const delta = 1; 
    const range = [];
    const rangeWithDots = [];
    let l;

    for (let i = 1; i <= totalPages; i++) {
      if (i === 1 || i === totalPages || (i >= currentPage - delta && i <= currentPage + delta)) {
        range.push(i);
      }
    }

    for (let i of range) {
      if (l) {
        if (i - l === 2) {
          rangeWithDots.push(l + 1);
        } else if (i - l !== 1) {
          rangeWithDots.push('...');
        }
      }
      rangeWithDots.push(i);
      l = i;
    }

    return rangeWithDots;
  };

  return (
    <div className="pagination-container">
        <div className="items-per-page">
            <span>Itens por página:</span>
            <select value={itemsPerPage} onChange={(e) => onItemsPerPageChange(Number(e.target.value))}>
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
            </select>
        </div>

        <nav>
          <ul className="pagination-wrapper">
            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                <button className="page-link" onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1}>Anterior</button>
            </li>
            
            {getPageNumbers().map((number, index) => (
                <li key={index} className={`page-item ${number === currentPage ? 'active' : ''} ${number === '...' ? 'disabled' : ''}`}>
                    {number === '...' ? (
                        <span className="page-link dots">...</span>
                    ) : (
                        <button className="page-link" onClick={() => onPageChange(number)}>{number}</button>
                    )}
                </li>
            ))}

            <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                <button className="page-link" onClick={() => onPageChange(currentPage + 1)} disabled={currentPage === totalPages}>Próxima</button>
            </li>
          </ul>
        </nav>
    </div>
  );
};

const ITEMS_PER_PAGE_DEFAULT = 5;
const DEPARTAMENTO_OPTIONS = ['People', 'TI', 'Comercial', 'Financeiro', 'CS', 'Marketing', 'Operacional'];
const ROLE_OPTIONS = ['Colaborador', 'Lider', 'G1', 'Master', 'Admin']; // Novo filtro
const STATUS_OPTIONS = [{ value: 'active', label: 'Ativo' }, { value: 'inactive', 'label': 'Inativo' }];

const Cadastro = () => {
    // Estados de Dados
    const [users, setUsers] = useState([]);
    const [pendingRegistrations, setPendingRegistrations] = useState([]);
    const [currentAdminData, setCurrentAdminData] = useState(null);
    
    // Estados de UI
    const [isLoading, setIsLoading] = useState(true);
    const [isUploading, setIsUploading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [isNewUserFlow, setIsNewUserFlow] = useState(false);
    
    // Filtros e Paginação
    const [itemsPerPage, setItemsPerPage] = useState(ITEMS_PER_PAGE_DEFAULT);
    const [currentPageUsers, setCurrentPageUsers] = useState(1);
    const [currentPagePending, setCurrentPagePending] = useState(1);
    
    // Filtros de Usuários
    const [searchTermUsers, setSearchTermUsers] = useState('');
    const [filterDepartamentoUsers, setFilterDepartamentoUsers] = useState('');
    const [filterStatusUsers, setFilterStatusUsers] = useState('');
    const [filterRoleUsers, setFilterRoleUsers] = useState(''); // Novo
    const [filterLojaUsers, setFilterLojaUsers] = useState(''); // Novo
    
    // Filtros de Pendentes
    const [searchTermPending, setSearchTermPending] = useState('');
    const [filterDepartamentoPending, setFilterDepartamentoPending] = useState('');
    
    // --- 1. SEGURANÇA: Verificar status do Admin logado ---
    useEffect(() => {
        const checkUserStatus = async () => {
          if (auth.currentUser) {
            try {
                const adminDocRef = doc(db, 'users', auth.currentUser.uid);
                const adminSnap = await getDoc(adminDocRef);
                
                if (adminSnap.exists()) { 
                    const data = adminSnap.data();
                    if (data.status === 'inactive') {
                        await signOut(auth);
                        alert("Seu usuário está inativo. Acesso negado.");
                        window.location.reload();
                        return;
                    }
                    setCurrentAdminData(data); 
                } else { 
                    console.error("Dados do administrador não encontrados."); 
                }
            } catch (error) {
                console.error("Erro ao verificar autenticação:", error);
            }
          }
        };
        checkUserStatus();
      }, []);
    
      // --- 2. BUSCAR DADOS ---
      const fetchUsersAndPending = useCallback(async () => {
        if (!currentAdminData?.empresa_id) {
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        try {
            const adminEmpresaId = currentAdminData.empresa_id;

            // A. Busca Usuários Públicos
            const usersCollectionRef = collection(db, 'users');
            const qUsers = query(usersCollectionRef, where("empresa_id", "==", adminEmpresaId));
            const usersSnapshot = await getDocs(qUsers);
            const usersPublicList = usersSnapshot.docs.map(d => ({ ...d.data(), id: d.id }));
    
            // B. Mescla com Dados Privados
            const mergedUsersList = await Promise.all(
                usersPublicList.map(async (publicUser) => {
                    const privateSnap = await getDoc(doc(db, 'usersprivado', publicUser.id));
                    const privateData = privateSnap.exists() ? privateSnap.data() : {};
                    
                    return { 
                        ...publicUser, 
                        ...privateData, 
                        status: publicUser.status || 'active', 
                        historicoAtualizacoes: privateData.historicoAtualizacoes || [], 
                        loja: publicUser.loja || '',
                        managerEmail: publicUser.managerEmail || '',
                        canViewSubordinates: publicUser.canViewSubordinates || false,
                        dataNascimento: privateData.dataNascimento || null, 
                        salario: privateData.salario || null,
                        exitData: privateData.exitData || null,
                        // Endereço vem do privado
                        cep: privateData.cep || '',
                        endereco: privateData.endereco || '',
                        numero: privateData.numero || '',
                        bairro: privateData.bairro || '',
                        cidade: privateData.cidade || '',
                        estado: privateData.estado || ''
                    };
                })
            );
            setUsers(mergedUsersList);
    
            // C. Busca Pendentes (Manual)
            const pendingCollectionRef = collection(db, 'pendingRegistrations');
            const qPending = query(pendingCollectionRef, where("empresa_id", "==", adminEmpresaId));
            const pendingSnapshot = await getDocs(qPending);
            const pendingList = pendingSnapshot.docs.map(d => ({ ...d.data(), id: d.id }));
            setPendingRegistrations(pendingList);
    
        } catch (error) { 
            console.error("Erro ao buscar dados:", error); 
        }
        setIsLoading(false);
      }, [currentAdminData]);
    
      useEffect(() => { fetchUsersAndPending(); }, [fetchUsersAndPending]);
      
      // Reseta página ao filtrar
      useEffect(() => { setCurrentPageUsers(1); }, [searchTermUsers, filterDepartamentoUsers, filterStatusUsers, filterRoleUsers, filterLojaUsers, itemsPerPage]);
      useEffect(() => { setCurrentPagePending(1); }, [searchTermPending, filterDepartamentoPending, itemsPerPage]);
    
    // --- 3. HELPERS ---
    const calculateAge = (dateInput) => {
        if (!dateInput) return '-';
        const birthDate = dateInput.toDate ? dateInput.toDate() : (dateInput instanceof Date ? dateInput : new Date(dateInput));
        if (isNaN(birthDate)) return '-';
        
        const today = new Date();
        let age = today.getFullYear() - birthDate.getFullYear();
        const m = today.getMonth() - birthDate.getMonth();
        if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
            age--;
        }
        return age;
    };

    const formatDateForTable = (timestamp) => {
        if (!timestamp) return '-';
        const date = timestamp.toDate ? timestamp.toDate() : (timestamp instanceof Date ? timestamp : null);
        if (!date || isNaN(date.getTime())) return '-';
        return date.toLocaleDateString('pt-BR', { timeZone: 'UTC' });
    };

    // Gera lista única de lojas para o filtro
    const uniqueLojas = useMemo(() => {
        const lojas = users.map(u => u.loja).filter(l => l && l.trim() !== '');
        return [...new Set(lojas)].sort();
    }, [users]);

    // --- FILTROS ---
    const filteredUsers = useMemo(() => {
        return users.filter(user => 
          (user.name?.toLowerCase().includes(searchTermUsers.toLowerCase()) || user.email?.toLowerCase().includes(searchTermUsers.toLowerCase())) &&
          (filterDepartamentoUsers === '' || user.departamento === filterDepartamentoUsers) &&
          (filterStatusUsers === '' || (user.status || 'active') === filterStatusUsers) &&
          (filterRoleUsers === '' || user.role === filterRoleUsers) && // Filtro Role
          (filterLojaUsers === '' || user.loja === filterLojaUsers)   // Filtro Loja
        );
      }, [users, searchTermUsers, filterDepartamentoUsers, filterStatusUsers, filterRoleUsers, filterLojaUsers]);
    
      const filteredPendingRegistrations = useMemo(() => {
        return pendingRegistrations.filter(reg =>
          (reg.name?.toLowerCase().includes(searchTermPending.toLowerCase()) || reg.email?.toLowerCase().includes(searchTermPending.toLowerCase())) &&
          (filterDepartamentoPending === '' || reg.departamento === filterDepartamentoPending)
        );
      }, [pendingRegistrations, searchTermPending, filterDepartamentoPending]);
    
    // --- HANDLERS ---
    
    const handleAddNewUser = () => { setEditingUser(null); setIsNewUserFlow(true); setShowModal(true); };
    const handleEditUser = (user) => { setEditingUser(user); setIsNewUserFlow(false); setShowModal(true); };

    const handleApprovePending = (reg) => {
        setEditingUser({
            ...reg,
            pendingRegistrationId: reg.id 
        });
        setIsNewUserFlow(true); 
        setShowModal(true);
    };
    
    const handleSetUserStatus = async (userId, currentStatus) => {
        const userToEdit = users.find(u => u.id === userId);
        if (!userToEdit) return;

        if (currentStatus === 'active') {
            setEditingUser({ ...userToEdit, status: 'inactive' });
            setIsNewUserFlow(false);
            setShowModal(true);
        } else {
            if (window.confirm(`Tem certeza que deseja reativar o acesso de ${userToEdit.name}?`)) {
                try {
                    const adminName = currentAdminData?.name || 'Admin';
                    const logMessage = `Status alterado de "Inativo" para "Ativo" por ${adminName}.`;
                    const newLogEntry = { data: new Date(), alteracao: logMessage, responsavelUID: auth.currentUser.uid, responsavelNome: adminName };
                    
                    const batch = writeBatch(db);
                    batch.update(doc(db, 'users', userId), { status: 'active', updatedAt: serverTimestamp() });
                    const userRef = doc(db, 'usersprivado', userId);
                    const existingHistory = userToEdit.historicoAtualizacoes || [];

                    batch.update(userRef, { 
                        updatedAt: serverTimestamp(), 
                        historicoAtualizacoes: [...existingHistory, newLogEntry], 
                        dataInativacao: null, 
                        exitData: null 
                    });
                    
                    await batch.commit();
                    fetchUsersAndPending();
                    alert(`Usuário ativado com sucesso.`);
                } catch (error) { 
                    alert("Erro ao ativar usuário: " + error.message); 
                }
            }
        }
    };
  
      const handleResendWelcomeEmail = async (user) => {
          if (!user || !user.email) { alert("Não foi possível encontrar o e-mail do usuário."); return; }
          if (window.confirm(`Reenviar e-mail de boas-vindas para ${user.name} (${user.email})?`)) {
              const functions = getFunctions(getApp(), 'southamerica-east1');
              const resendEmailFunction = httpsCallable(functions, 'resendWelcomeEmail');
              try {
                  const result = await resendEmailFunction({ userId: user.id });
                  alert(result.data.message || 'E-mail reenviado com sucesso!');
              } catch (error) { alert(`Erro ao reenviar e-mail: ${error.message}`); }
          }
      };
    
      const handleDeletePendingRegistration = async (regId) => {
        if (window.confirm(`Rejeitar e remover esta pré-inscrição?`)) {
          try {
            await deleteDoc(doc(db, 'pendingRegistrations', regId));
            setPendingRegistrations(prev => prev.filter(p => p.id !== regId));
          } catch (error) { alert("Erro ao remover: " + error.message); }
        }
      };
    
      const handleModalClose = () => { setShowModal(false); setEditingUser(null); setIsNewUserFlow(false); };
    
      // --- SALVAR DADOS (Do Modal) ---
      const handleModalSave = async (formData) => {
          if (!currentAdminData?.empresa_id) { alert("ID da empresa não identificado."); return; }
          
          const { 
              name, email, departamento, role, funcao, salario, 
              dataEntrada, dataNascimento, modeloContrato, status: formStatus, beneficios, descontos, sendInvite,
              loja, managerEmail, canViewSubordinates, exitData,
              // Campos de Endereço Novos
              cep, endereco, numero, bairro, cidade, estado
          } = formData;

          const parsedDataEntrada = dataEntrada ? new Date(dataEntrada) : null;
          const parsedDataNascimento = dataNascimento ? new Date(dataNascimento) : null;
      
          if (isNewUserFlow) {
            try {
              await addDoc(collection(db, "pendingRegistrations"), { 
                  name, email, departamento, role, funcao, salario, 
                  dataEntrada: parsedDataEntrada,
                  dataNascimento: parsedDataNascimento,
                  loja, managerEmail, canViewSubordinates,
                  modeloContrato, beneficios, descontos,
                  // Salva endereço no pendente também
                  cep, endereco, numero, bairro, cidade, estado,
                  empresa_id: currentAdminData.empresa_id, status: 'pending_auth_creation', requestedBy: auth.currentUser.uid, 
                  requestedAt: serverTimestamp(), sendInviteEmail: sendInvite 
              });

              if (editingUser?.pendingRegistrationId) {
                  await deleteDoc(doc(db, 'pendingRegistrations', editingUser.pendingRegistrationId));
              }

              alert('Solicitação enviada! O usuário será criado em instantes.');
              fetchUsersAndPending();
            } catch (error) { alert("Erro: " + error.message); }
          
          } else if (editingUser?.id) {
            try {
              const batch = writeBatch(db);
              
              // Dados Públicos (Mantém o básico)
              batch.update(doc(db, 'users', editingUser.id), { 
                  name, email, departamento, role, funcao, 
                  loja, managerEmail, canViewSubordinates,
                  status: formStatus || editingUser.status || 'active', 
                  empresa_id: currentAdminData.empresa_id, 
                  updatedAt: serverTimestamp() 
              });

              // Dados Privados (Inclui Endereço e Financeiro)
              const privateUpdateData = { 
                  salario: parseFloat(salario) || 0, 
                  dataEntrada: parsedDataEntrada, 
                  dataNascimento: parsedDataNascimento,
                  modeloContrato, beneficios, descontos, 
                  // Salva dados de endereço no privado
                  cep, endereco, numero, bairro, cidade, estado,
                  updatedAt: serverTimestamp() 
              };

              if (formStatus === 'inactive' && exitData) {
                  privateUpdateData.exitData = exitData;
                  privateUpdateData.dataInativacao = exitData.exitDate || new Date();
                  
                  const adminName = currentAdminData?.name || 'Admin';
                  const logMessage = `Usuário inativado. Motivo: ${exitData.reason}. Tipo: ${exitData.type}.`;
                  const newLogEntry = { data: new Date(), alteracao: logMessage, responsavelUID: auth.currentUser.uid, responsavelNome: adminName };
                  
                  const currentHistory = editingUser.historicoAtualizacoes || [];
                  privateUpdateData.historicoAtualizacoes = [...currentHistory, newLogEntry];
              }

              batch.update(doc(db, 'usersprivado', editingUser.id), privateUpdateData);

              await batch.commit();
              alert('Dados atualizados!');
              fetchUsersAndPending();
  
              if (sendInvite && editingUser.email) {
                  try {
                      await sendPasswordResetEmail(auth, editingUser.email);
                      alert(`Senha enviada para ${editingUser.email}.`);
                  } catch (e) { alert("Erro ao enviar senha: " + e.message); }
              }
            } catch (error) { alert("Erro ao atualizar: " + error.message); }
          }
          handleModalClose();
      };

    const handleDownloadTemplate = () => {
        const googleSheetTemplateLink = "https://docs.google.com/spreadsheets/u/2/d/122vMvIQWr_qVVkDvfpJepSLWvU4UpcUVqeRH_aiDQSo/copy";
        window.open(googleSheetTemplateLink, '_blank');
    };
    
    // --- UPLOAD EM MASSA ---
    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setIsUploading(true);
        const reader = new FileReader();
        reader.onload = (evt) => {
            try {
                const bstr = evt.target.result;
                const wb = XLSX.read(bstr, { type: 'binary', cellDates: true });
                const wsname = wb.SheetNames[0]; 
                const ws = wb.Sheets[wsname];
                const data = XLSX.utils.sheet_to_json(ws, { raw: false });
                processAndSaveBulkData(data);
            } catch (error) {
                console.error("Erro ao ler arquivo:", error);
                alert(`Erro: ${error.message}`);
                setIsUploading(false);
            }
        };
        reader.readAsBinaryString(file);
        e.target.value = '';
    };

    const processAndSaveBulkData = async (rows) => {
        if (!currentAdminData?.empresa_id) {
            alert("ID da empresa não encontrado.");
            setIsUploading(false);
            return;
        }

        const validRows = [];
        const errors = [];
        
        const parseExcelDate = (input) => {
            if (!input) return null;
            if (input instanceof Date) return new Date(Date.UTC(input.getFullYear(), input.getMonth(), input.getDate(), 12, 0, 0));
            if (typeof input === 'string') {
                const parts = input.split('/');
                if (parts.length === 3) return new Date(Date.UTC(parts[2], parts[1] - 1, parts[0], 12, 0, 0));
            }
            return null;
        };

        rows.forEach((row, index) => {
            const name = row["Nome Completo*"];
            const email = row["Email*"];
            if (!name || !email) {
                errors.push(`Linha ${index + 2}: Nome ou Email faltando.`);
                return;
            }

            const beneficios = [];
            for (let i = 1; i <= 3; i++) {
                const nome = row[`Benefício ${i} (Nome)`];
                const valor = row[`Benefício ${i} (Valor)`];
                if (nome) beneficios.push({ nome: String(nome), valor: parseFloat(valor) || 0 });
            }
            const descontos = [];
            for (let i = 1; i <= 3; i++) {
                const nome = row[`Desconto ${i} (Nome)`];
                const valor = row[`Desconto ${i} (Valor)`];
                if (nome) descontos.push({ nome: String(nome), valor: parseFloat(valor) || 0 });
            }

            validRows.push({
                publicData: {
                    name: String(name), email: String(email), 
                    departamento: String(row["Departamento*"]), role: String(row["Nível de Acesso (Role)*"]), 
                    funcao: String(row["Função (Cargo)"]||''), loja: String(row["Loja"]||''), 
                    managerEmail: String(row["Email do Gestor"]||''), 
                    canViewSubordinates: String(row["Visualizar Subordinados? (Sim/Não)"]).toLowerCase().trim() === 'sim',
                    empresa_id: currentAdminData.empresa_id, 
                    status: 'active',
                    updatedAt: serverTimestamp(),
                },
                privateData: {
                    salario: parseFloat(row["Salário Bruto (R$)"])||0,
                    dataEntrada: parseExcelDate(row["Data de Entrada (dd/mm/yyyy)"]),
                    dataNascimento: parseExcelDate(row["Data de Nascimento (dd/mm/yyyy)"]),
                    modeloContrato: String(row["Modelo de Contrato*"]),
                    beneficios, descontos, updatedAt: serverTimestamp(),
                }
            });
        });
        
        if (errors.length > 0) { alert("Erros:\n" + errors.join("\n")); setIsUploading(false); return; }
        if (validRows.length === 0) { alert("Nenhum dado válido."); setIsUploading(false); return; }

        try {
            const batch = writeBatch(db);
            validRows.forEach(row => {
                const newDocRef = doc(collection(db, "users")); 
                batch.set(newDocRef, row.publicData);
                batch.set(doc(db, "usersprivado", newDocRef.id), row.privateData);
            });

            await batch.commit();
            alert(`${validRows.length} usuários importados com sucesso!`);
            fetchUsersAndPending();
        } catch (error) {
            console.error("Erro upload:", error);
            alert("Erro ao salvar: " + error.message);
        } finally {
            setIsUploading(false);
        }
    };
    
    if (isLoading || !currentAdminData) { return <p className="cadastro-loading">Carregando dados...</p>; }

    const currentUsersToDisplay = filteredUsers.slice((currentPageUsers - 1) * itemsPerPage, currentPageUsers * itemsPerPage);
    const totalPagesUsers = Math.ceil(filteredUsers.length / itemsPerPage);
    
    const currentPendingToDisplay = filteredPendingRegistrations.slice((currentPagePending - 1) * itemsPerPage, currentPagePending * itemsPerPage);
    const totalPagesPending = Math.ceil(filteredPendingRegistrations.length / itemsPerPage);

    return (
        <div className="cadastro-container">
          <div className="cadastro-header">
            <h1 className="cadastro-title">Gerenciamento de Usuários</h1>
            <div className="cadastro-header-actions">
                <button onClick={handleDownloadTemplate} className="cadastro-button secondary">
                    <FaDownload /> Baixar Modelo
                </button>
                <label htmlFor="bulk-upload-input" className="cadastro-button secondary">
                    <FaUpload /> {isUploading ? 'Processando...' : 'Enviar Planilha'}
                </label>
                <input 
                    id="bulk-upload-input"
                    type="file" 
                    accept=".xlsx, .csv" 
                    style={{ display: 'none' }} 
                    onChange={handleFileChange}
                    disabled={isUploading}
                />
                <button onClick={handleAddNewUser} className="cadastro-add-button">
                    <FaUserPlus /> Novo Usuário
                </button>
            </div>
          </div>
    
          <h2 className="cadastro-subtitle">Usuários Registrados</h2>
          <div className="cadastro-filters-container">
            {/* SEARCH */}
            <input type="text" placeholder="Buscar por Nome ou Email..." value={searchTermUsers} onChange={(e) => setSearchTermUsers(e.target.value)} className="form-control" />
            
            {/* FILTRO DEPARTAMENTO */}
            <select value={filterDepartamentoUsers} onChange={(e) => setFilterDepartamentoUsers(e.target.value)} className="form-select">
              <option value="">Depto (Todos)</option>
              {DEPARTAMENTO_OPTIONS.map(dep => <option key={dep} value={dep}>{dep}</option>)}
            </select>
            
            {/* FILTRO ROLE (Novo) */}
            <select value={filterRoleUsers} onChange={(e) => setFilterRoleUsers(e.target.value)} className="form-select">
              <option value="">Acesso (Todos)</option>
              {ROLE_OPTIONS.map(role => <option key={role} value={role}>{role}</option>)}
            </select>

            {/* FILTRO LOJA (Novo - Dinâmico) */}
            <select value={filterLojaUsers} onChange={(e) => setFilterLojaUsers(e.target.value)} className="form-select">
              <option value="">Loja (Todas)</option>
              {uniqueLojas.map(l => <option key={l} value={l}>{l}</option>)}
            </select>

            {/* FILTRO STATUS */}
            <select value={filterStatusUsers} onChange={(e) => setFilterStatusUsers(e.target.value)} className="form-select">
              <option value="">Status (Todos)</option>
              {STATUS_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
            </select>
          </div>
    
          <div className="table-responsive-wrapper">
            <table className="cadastro-table">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Loja</th>
                  <th>Idade</th>
                  <th>Função</th>
                  <th>Gestor</th>
                  <th>Salário (R$)</th>
                  <th>Status</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {currentUsersToDisplay.length > 0 ? currentUsersToDisplay.map(user => (
                  <tr key={user.id} className={user.status === 'inactive' ? 'cadastro-user-inactive' : ''}>
                    <td data-label="Nome">
                        {user.name}<br/>
                        <span style={{fontSize: '0.8em', color: '#666'}}>{user.email}</span>
                    </td>
                    <td data-label="Loja">{user.loja || '-'}</td>
                    <td data-label="Idade">{calculateAge(user.dataNascimento)} anos</td>
                    <td data-label="Função">{user.funcao || '-'}</td>
                    <td data-label="Gestor">
                        {user.managerEmail ? <span style={{fontSize:'0.85em'}}>{user.managerEmail}</span> : '-'}
                    </td>
                    <td data-label="Salário (R$)">{user.salario ? (typeof user.salario === 'number' ? user.salario.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : user.salario) : '-'}</td>
                    <td data-label="Status"><span className={`cadastro-status-badge status-${user.status || 'active'}`}>{user.status === 'inactive' ? 'Inativo' : 'Ativo'}</span></td>
                    <td className="cadastro-actions">
                      <button onClick={() => handleSetUserStatus(user.id, user.status || 'active')} className={`cadastro-action-button ${user.status === 'inactive' ? 'activate' : 'inactivate'}`} title={user.status === 'inactive' ? 'Ativar' : 'Inativar'}>{user.status === 'inactive' ? <FaToggleOn /> : <FaToggleOff />}</button>
                      <button onClick={() => handleEditUser(user)} className="cadastro-action-button edit" title="Editar"><FaEdit /></button>
                      <button onClick={() => handleResendWelcomeEmail(user)} className="cadastro-action-button invite" title="Reenviar Convite"><FaEnvelope /></button>
                    </td>
                  </tr>
                )) : (
                  <tr><td colSpan="8" className="cadastro-no-users">Nenhum usuário encontrado.</td></tr>
                )}
              </tbody>
            </table>
          </div>
          
          <Pagination 
            currentPage={currentPageUsers} 
            totalPages={totalPagesUsers} 
            onPageChange={setCurrentPageUsers}
            itemsPerPage={itemsPerPage}
            onItemsPerPageChange={setItemsPerPage}
          />
    
          {pendingRegistrations.length > 0 && (
            <>
              <h2 className="cadastro-subtitle" style={{marginTop: '40px'}}>Pré-Cadastros Pendentes (Manuais)</h2>
              <div className="table-responsive-wrapper">
                <table className="cadastro-table">
                  <thead>
                    <tr><th>Nome</th><th>Email</th><th>Depto.</th><th>Prev. Entrada</th><th>Ações</th></tr>
                  </thead>
                  <tbody>
                    {currentPendingToDisplay.map(reg => (
                      <tr key={reg.id}>
                        <td data-label="Nome">{reg.name}</td>
                        <td data-label="Email">{reg.email}</td>
                        <td data-label="Depto.">{reg.departamento}</td>
                        <td data-label="Prev. Entrada">{formatDateForTable(reg.dataEntrada)}</td>
                        <td className="cadastro-actions">
                            <button onClick={() => handleApprovePending(reg)} className="cadastro-action-button activate" title="Aprovar e Criar"><FaCheck /></button>
                            <button onClick={() => handleDeletePendingRegistration(reg.id)} className="cadastro-action-button delete" title="Rejeitar"><FaTrashAlt /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Pagination 
                currentPage={currentPagePending} 
                totalPages={totalPagesPending} 
                onPageChange={setCurrentPagePending}
                itemsPerPage={itemsPerPage}
                onItemsPerPageChange={setItemsPerPage}
              />
            </>
          )}
    
          {showModal && (
            <UserFormModal 
                user={editingUser} 
                isNewUser={isNewUserFlow} 
                onClose={handleModalClose} 
                onSave={handleModalSave}
                allUsers={users} 
            />
          )}
        </div>
      );
};

export default Cadastro;